import type { ComparisonViewProps } from '../../types';

export function ComparisonView({ playerStats, comparisonStats, loading = false }: ComparisonViewProps) {
  // Show loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded mb-4 w-1/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="h-32 bg-gray-200 rounded"></div>
              <div className="h-32 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show empty state if no comparison data
  if (!comparisonStats || !playerStats) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">
          League Comparison
        </h3>
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h4 className="text-lg font-medium text-gray-600 mb-2">No Comparison Data</h4>
          <p className="text-gray-500">
            Unable to load league comparison statistics.
          </p>
        </div>
      </div>
    );
  }

  const { player_average, league_average, percentage_difference, is_above_average, player_rank, total_players } = comparisonStats;

  return (
    <div className="space-y-6">
      {/* Main Comparison Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-6">
          League Comparison
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Player Average */}
          <div className="text-center p-6 bg-blue-50 rounded-lg">
            <div className="text-3xl font-bold text-blue-600 mb-2">
              {player_average.toFixed(1)}
            </div>
            <div className="text-sm font-medium text-blue-800 mb-1">
              Your Average
            </div>
            <div className="text-xs text-blue-600">
              Based on {playerStats.total_games} games
            </div>
          </div>

          {/* League Average */}
          <div className="text-center p-6 bg-gray-50 rounded-lg">
            <div className="text-3xl font-bold text-gray-600 mb-2">
              {league_average.toFixed(1)}
            </div>
            <div className="text-sm font-medium text-gray-800 mb-1">
              League Average
            </div>
            <div className="text-xs text-gray-600">
              Excluding your scores
            </div>
          </div>
        </div>

        {/* Performance Indicator */}
        <div className="mt-6 text-center">
          <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${
            is_above_average 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {is_above_average ? (
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            ) : (
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
              </svg>
            )}
            {Math.abs(percentage_difference).toFixed(1)}% {is_above_average ? 'Above' : 'Below'} League Average
          </div>
        </div>
      </div>

      {/* Detailed Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Ranking */}
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <div className="text-2xl font-bold text-purple-600 mb-2">
            #{player_rank}
          </div>
          <div className="text-sm font-medium text-gray-800 mb-1">
            League Rank
          </div>
          <div className="text-xs text-gray-600">
            Out of {total_players} players
          </div>
        </div>

        {/* Points Difference */}
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <div className={`text-2xl font-bold mb-2 ${
            is_above_average ? 'text-green-600' : 'text-red-600'
          }`}>
            {is_above_average ? '+' : ''}{(player_average - league_average).toFixed(1)}
          </div>
          <div className="text-sm font-medium text-gray-800 mb-1">
            Points Difference
          </div>
          <div className="text-xs text-gray-600">
            Per game vs league
          </div>
        </div>

        {/* Performance Level */}
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <div className={`text-2xl font-bold mb-2 ${
            percentage_difference >= 10 ? 'text-green-600' :
            percentage_difference >= 0 ? 'text-blue-600' :
            percentage_difference >= -10 ? 'text-orange-600' : 'text-red-600'
          }`}>
            {percentage_difference >= 10 ? 'Excellent' :
             percentage_difference >= 0 ? 'Above Avg' :
             percentage_difference >= -10 ? 'Below Avg' : 'Needs Work'}
          </div>
          <div className="text-sm font-medium text-gray-800 mb-1">
            Performance Level
          </div>
          <div className="text-xs text-gray-600">
            Relative to league
          </div>
        </div>
      </div>

      {/* Visual Progress Bar */}
      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="text-md font-semibold text-gray-800 mb-4">
          Performance Visualization
        </h4>
        
        <div className="relative">
          {/* Progress bar background */}
          <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
            <div 
              className={`h-4 rounded-full transition-all duration-1000 ${
                is_above_average ? 'bg-green-500' : 'bg-red-500'
              }`}
              style={{ 
                width: `${Math.min(100, Math.max(10, 50 + (percentage_difference * 2)))}%` 
              }}
            ></div>
          </div>
          
          {/* Scale markers */}
          <div className="flex justify-between text-xs text-gray-500 mb-2">
            <span>Much Below</span>
            <span>League Average</span>
            <span>Much Above</span>
          </div>
          
          {/* Current position indicator */}
          <div className="text-center">
            <div className={`inline-block px-3 py-1 rounded text-xs font-medium ${
              is_above_average ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              You are here: {player_average.toFixed(1)} avg
            </div>
          </div>
        </div>
      </div>

      {/* Insights and Recommendations */}
      <div className="bg-white rounded-lg shadow p-6">
        <h4 className="text-md font-semibold text-gray-800 mb-4">
          Performance Insights
        </h4>
        
        <div className="space-y-3">
          {is_above_average ? (
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800">
                  Great Performance!
                </p>
                <p className="text-sm text-gray-600">
                  You're bowling {(player_average - league_average).toFixed(1)} points above the league average. 
                  Keep up the excellent work!
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800">
                  Room for Improvement
                </p>
                <p className="text-sm text-gray-600">
                  You're {Math.abs(player_average - league_average).toFixed(1)} points below the league average. 
                  Focus on consistency and technique to improve your scores.
                </p>
              </div>
            </div>
          )}
          
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-800">
                League Standing
              </p>
              <p className="text-sm text-gray-600">
                You rank #{player_rank} out of {total_players} players in the league. 
                {(player_rank && total_players) ? (
                  player_rank <= Math.ceil(total_players * 0.25) ? ' You\'re in the top 25%!' :
                  player_rank <= Math.ceil(total_players * 0.5) ? ' You\'re in the top half of the league.' :
                  ' There\'s room to climb the rankings.'
                ) : ''}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}